<template>
  <v-card height="unset">
    <v-list>
      <v-list-item>
        <analyze-search
          id="snapshot-text-search"
          v-model="search"
          date-range-picker
          :initial-date-range="dateRange"
          @input:date-range="dateRange = $event"
        >
          <template #date-range-label>
            {{ labels.SNAPSHOT_BAR.DATE }}
          </template>
        </analyze-search>

        <v-chip
          v-if="dateRange && dateRange.length > 0"
          class="w-100 justify-space-between"
          variant="outlined"
          closable
          label
          justify="end"
          @click:close="resetSearch()"
        >
          <p v-if="!HELPERS.isSameDate(dateRange[0], dateRange[1])">
            {{ HELPERS.formatDateNoTime(dateRange[0].toISOString()) }} -
            {{ HELPERS.formatDateNoTime(dateRange[1].toISOString()) }}
          </p>
          <p v-else>
            {{ HELPERS.formatDateNoTime(dateRange[0].toISOString()) }}
          </p>
        </v-chip>
      </v-list-item>
    </v-list>

    <v-divider />

    <v-list max-height="300" style="overflow-y: scroll">
      <v-list-item
        v-for="(snap, index) in filteredSnapshots"
        :id="`item-${index}`"
        :key="index"
        class="snapshot-item"
        :class="highlightSelectedSnapshot(snap)"
        :disabled="snap.name === props.currentSnapshot?.name"
        @click="selectSnapshot(snap)"
      >
        <v-list-item-title
          :id="`item-title-${index}`"
          :class="snap.name === props.currentSnapshot?.name ? 'font-weight-bold' : ''"
        >
          {{ HELPERS.formatDateNoTime(new Date(snap?.date)?.toISOString()) }} / {{ snap?.name }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item v-if="filteredSnapshots.length === 0">
        {{ labels.SNAPSHOT_BAR.NO_SNAPSHOTS_AVAILABLE }}
      </v-list-item>
    </v-list>
  </v-card>
</template>

<script lang="ts" setup>
  import { PropType, computed, ref } from 'vue'
  import moment from 'moment'
  import labels from '@/translations/labels.json'
  import { Snapshot } from '@/models'
  import { useSnapshotStore } from '@/stores/snapshotStore'
  import { useRoute, useRouter } from 'vue-router'
  import { useRouteStore } from '@/stores/routeStore'
  import * as HELPERS from '@/utils/helpers'
  import CONSTANTS from '@/constants/Config.constants'
  import AnalyzeSearch from '@/components/Inputs/AnalyzeSearch.vue'
  import { onMounted } from 'vue'
  import '@vuepic/vue-datepicker/dist/main.css'

  const route = useRoute()
  const router = useRouter()

  const routeStore = useRouteStore()
  const snapshotStore = useSnapshotStore()

  const emit = defineEmits<{
    (event: 'closeSnapshotMenu', value: boolean): void
  }>()

  const props = defineProps({
    currentSnapshot: { type: Object as PropType<Snapshot | undefined>, required: true },
  })

  onMounted(() => {
    const startDate = new Date(2019, 6, 17)
    const endDate = new Date()
    snapshotStore.setFilterDateRange([])
    dateRange.value = [startDate, endDate]
  })

  const search = ref<string>('')
  const dateRange = ref<[Date, Date]>()

  const filteredSnapshots = computed((): Snapshot[] => {
    if (dateRange.value && dateRange.value.length > 0) {
      snapshotStore.setFilterDateRange([
        moment(dateRange.value[0]?.toISOString()),
        moment(dateRange.value[1]?.toISOString()),
      ])
    }

    snapshotStore.setFilterString(search.value)
    snapshotStore.setSortCriteria(CONSTANTS.SORTING_OPTIONS[0])
    return snapshotStore.getSortedAndFiltered
  })

  function resetSearch(): void {
    snapshotStore.setFilterDateRange([])
    dateRange.value = undefined
  }

  function selectSnapshot(selectedSnapshot: Snapshot): void {
    snapshotStore.setCurrentSnapshot(selectedSnapshot)

    const message = HELPERS.replaceVariableInString(
      labels.ALERT_VIEW.SNAPSHOT_SELECTED_ALERT,
      selectedSnapshot ? selectedSnapshot.name : '',
    )

    HELPERS.showTopAlertMessage(true, 'info', message)

    if (route.params.snapshotId !== undefined) {
      router.push({
        params: { snapshotId: selectedSnapshot.id.toString() },
        query: {
          filter: router.currentRoute?.value?.query?.filter,
        },
      })
      routeStore.setSnapshotId(selectedSnapshot.id.toString())
    }

    if (route.fullPath.includes('/artifact/')) {
      router.push({
        name: labels.SIDEBAR.MAIN.EXPLORER,
        params: { snapshotId: selectedSnapshot.id.toString() },
        query: {
          filter: router.currentRoute?.value?.query?.filter,
        },
      })
      routeStore.setArtifactId(undefined)
    }

    emit('closeSnapshotMenu', false)
  }

  function highlightSelectedSnapshot(snap: Snapshot): string {
    if (snap.name === props.currentSnapshot?.name) {
      return 'highlight-selected-snapshot'
    }

    return ''
  }
</script>

<style lang="scss" scoped>
  // used dynamically (title color change)
  .highlight-selected-snapshot {
    background-color: rgb(var(--v-theme-itemis-analyze-secondary));
  }

  .snapshot-item {
    &:hover {
      background-color: rgb(var(--v-theme-itemis-analyze-secondary));
    }
  }
</style>
